<template>
  <form ref="$form" novalidate @submit.prevent="onSubmit">
    <input type="hidden" name="form_id" :value="content.formId" />
    <input type="hidden" name="form_type" :value="content.type" />
    <input type="hidden" name="referrer" :value="navigation.referrer" />
    <VInput
      v-model="form.firstName"
      :label="t('form-firstname')"
      name="firstname"
      class="form-field"
    />
    <VInput
      v-model="form.lastName"
      :label="t('form-lastname')"
      name="lastname"
      class="form-field"
    />
    <VInput
      v-model="form.email"
      :label="t('form-email')"
      name="email"
      type="email"
      class="form-field"
    />
    <VInput
      v-model="form.phone"
      :required="false"
      name="phone"
      :label="t('form-phone')"
      class="form-field"
    />

    <VTextarea
      id="textarea"
      v-model="form.message"
      name="description"
      :label="t('form-description')"
      class="form-field"
    />

    <!-- eslint-disable  -->
    <VCheckbox
      v-model="form.newsLetter"
      id="newsletter"
      name="newsletter"
      :required="false"
      :label="t('form-newsletter')"
      class="form-field"
    />
    <!-- eslint-enable  -->
    <Feedback v-if="hasError" :content="feedback" class="form-field" />
    <div class="turnstile-widget"></div>
    <div class="row end-xxs">
      <GBtn :disabled="isLoading" type="submit">{{ send }}</GBtn>
    </div>
  </form>
</template>

<script setup lang="ts">
import { useAxios } from '@monofront/vue-axios-plugin'
import { computed, reactive, ref, onMounted } from 'vue'
import { useRouter } from 'vue-router'

import Feedback from '@/components/ui/Feedback.vue'
import VCheckbox from '@/components/form/Checkbox.vue'
import VInput from '@/components/form/Input.vue'
import VTextarea from '@/components/form/Textarea.vue'
import {
  getTurnstileToken,
  removeTurnstile,
  stringifyFormDataNewsletter,
  validateForm,
} from '@/modules/form'
import { useNavigationStore } from '@/stores/navigation'
import { useChromeStore } from '@/stores/chrome'
import { logger } from '@/utils/logger'
import { pushForm, formStart } from '@/utils/tracking'

const props = defineProps({
  content: {
    type: Object,
    required: true,
  },
})

const axios = useAxios()
const navigation = useNavigationStore()
const { t, ...chrome } = useChromeStore()
const feedback = reactive({
  type: 'warning',
  text: t('form-error'),
})

const form = reactive({
  firstName: '',
  lastName: '',
  email: '',
  message: '',
  phone: '',
  newsLetter: false,
})

const router = useRouter()
const $form = ref<HTMLFormElement>({} as HTMLFormElement)
const isLoading = ref(false)
const hasError = ref(false)

const send = computed(() =>
  isLoading.value ? t('form-sending') : t('form-send')
)

onMounted(() => {
  formStart($form.value)
})

const onSubmit = async () => {
  pushForm('click')

  const isValid = validateForm($form.value)

  if (!isValid) {
    return
  }

  isLoading.value = true
  hasError.value = false
  const formData = new FormData($form.value)
  formData.append('tags', 'website,contact-form,divers')
  let turnstileWidgetID: string

  try {
    const { id, token } = await getTurnstileToken()

    if (token) {
      turnstileWidgetID = id
      formData.append('cf-turnstile-response', token)
    }
  } catch (error) {
    console.error(error)
  }

  stringifyFormDataNewsletter(formData)

  axios
    .post(
      `${import.meta.env.VITE_API_DOMAIN + props.content.submitUrl}`,
      formData
    )
    .catch(res => {
      hasError.value = true
      pushForm('error')
      logger.error(res)
      isLoading.value = false

      return res
    })
    .then(res => {
      if (res.data?.status === 200) {
        pushForm('success')
        pushForm('success', formData)

        router.push({
          name: `thank-you-${chrome.language}`,
          params: {
            segment: chrome.language === 'fr' ? 'merci' : 'thank-you',
          },
          // https://github.com/vuejs/router/blob/main/packages/router/CHANGELOG.md
          // params: {
          //   content: { type: props.content.type },
          // },
          query: {
            type: props.content.type,
          },
        })
      } else {
        hasError.value = true
        pushForm('error')
        logger.error(res)
        isLoading.value = false
      }
    })
    .finally(() => {
      removeTurnstile(turnstileWidgetID)
    })
}
</script>

<style lang="scss" scoped>
.type {
  @include mq(xs) {
    margin-bottom: 0;
  }
}
</style>
